import { computePosition, flip, shift, offset, arrow, autoUpdate } from "@floating-ui/dom"

// Shared Floating-UI positioning for ".floating-panel" popovers — the chrome
// worn by the [[ ]] reference picker dropdown and the entity hover-card. Owns the
// one fiddly thing both need: pin a fixed-position panel to a reference, keep it
// on-screen (flip/shift), and place the little arrow on the panel's
// reference-facing edge with the right data-edge for the CSS.
//
// `reference` is anything with getBoundingClientRect() — a real element or a
// Floating-UI virtual element (the picker anchors to the text caret). The panel
// MUST be position:fixed with width:max-content already set by the caller (per
// Floating UI's docs, so a container can't resize it mid-measure).
//
//   const stop = anchorPanel(linkEl, panelEl, arrowEl, { placement: "top" })
//   // … later, when the panel closes:
//   stop()
//
// Returns the autoUpdate teardown — call it to stop re-anchoring on scroll/resize
// (and do it before removing the panel, or the observers leak).
export function anchorPanel(reference, panel, arrowEl, { placement = "top-start", gap = 8 } = {}) {
  return autoUpdate(reference, panel, () => {
    computePosition(reference, panel, {
      strategy: "fixed", // MUST match the panel's position:fixed
      placement,
      middleware: [
        offset(gap),
        flip(),
        shift({ padding: 8 }),
        // padding keeps the arrow off the rounded corners.
        arrow({ element: arrowEl, padding: 12 })
      ]
    }).then(({ x, y, placement: resolved, middlewareData }) => {
      Object.assign(panel.style, { left: `${x}px`, top: `${y}px` })
      positionArrow(arrowEl, resolved, middlewareData.arrow)
    })
  })
}

// Place the arrow along the panel's reference-facing edge. When the panel sits
// ABOVE the reference (placement starts "top"), the arrow is on the panel's
// BOTTOM edge, pointing down; when BELOW, on the TOP edge, pointing up. data-edge
// names the arrow's own edge, which the CSS reads to draw the outward borders.
function positionArrow(arrowEl, placement, arrowData) {
  if (!arrowEl || !arrowData) return
  const above = placement.startsWith("top") // panel above the reference

  arrowEl.style.left = arrowData.x != null ? `${arrowData.x}px` : ""
  arrowEl.style.top = ""
  arrowEl.style.bottom = ""
  if (above) {
    arrowEl.style.top = "100%" // arrow on the panel's BOTTOM edge
    arrowEl.dataset.edge = "bottom"
  } else {
    arrowEl.style.bottom = "100%" // arrow on the panel's TOP edge
    arrowEl.dataset.edge = "top"
  }
}
